import { useState, useRef, useEffect } from 'react';
import clsx from 'clsx';
import { MdArrowDropDown, MdArrowDropUp } from 'react-icons/md';
import Checkbox from '@/components/Checkbox';

type AdminMultiSelectOption = {
  id: string | number;
  name: string;
};

type AdminMultiSelectDropdownProps = {
  label: string;
  options: AdminMultiSelectOption[];
  value: (string | number)[];
  onChange: (value: (string | number)[]) => void;
};

export default function AdminMultiSelectDropdown({
  label,
  options,
  value,
  onChange,
}: AdminMultiSelectDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // 外側クリックで閉じる
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleCheckboxChange = (optionId: string | number) => {
    if (value.includes(optionId)) {
      onChange(value.filter((id) => id !== optionId));
    } else {
      onChange([...value, optionId]);
    }
  };

  return (
    <div ref={dropdownRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={clsx(
          'flex h-[42px] w-full cursor-pointer items-center justify-between gap-xxs rounded border border-solid border-[#d1d1d1] bg-white px-3 text-button-md',
          isOpen && 'bg-[#e7e7e7]'
        )}
      >
        <span className="whitespace-nowrap">{label}</span>
        {isOpen ? <MdArrowDropUp className="size-4" /> : <MdArrowDropDown className="size-4" />}
      </button>

      {isOpen && (
        <div className="absolute top-[46px] left-0 z-50 w-[236px] overflow-hidden rounded border border-solid border-[#ccc] bg-white shadow-[0px_0px_12px_0px_rgba(0,0,0,0.2)]">
          {options.map((option) => (
            <div
              key={option.id}
              onClick={() => handleCheckboxChange(option.id)}
              className={clsx(
                'group flex w-full cursor-pointer items-stretch overflow-hidden px-sm py-2',
                'hover:bg-[#f6f6f6]'
              )}
            >
              <Checkbox
                checked={value.includes(option.id)}
                onChange={() => handleCheckboxChange(option.id)}
              >
                <span className="truncate">{option.name}</span>
              </Checkbox>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
